<template>
  <div class="chapterOne">
      <div style="marginTop:50px;fontSize:22px;fontWeight:bold;">第1章 什么是AI？（第2节 AI相关领域）</div>
      <div style="marginTop:30px;fontSize:12px;color:rgb(153,153,153);">2020-3-14 20:12:32</div>
      <div class="text fontBig" style="marginTop:70px;">机器人会取代我的工作吗？人工智能（简称AI）在未来十年内如何可能改变我的工作？AI技术现在在哪些地方使用，以后会在哪些地方使用？</div>
      <div class="text fontBig">在第 1 章中，我们将介绍以下部分，点击链接可以进入：</div>
      <div class="fontBig" style="color:#0090ff;cursor: pointer;" @click="jumpOne">一、<span style="color:#0090ff;textDecoration:underline;">如何定义AI？</span></div>
      <div class="fontBig" style="color:#0090ff;cursor: pointer;"  @click="jumpTow">二、<span style="color:#0090ff;textDecoration:underline;">AI相关领域</span></div>
      <div class="fontBig" style="color:#0090ff;cursor: pointer;"  @click="jumpThree">三、<span style="color:#0090ff;textDecoration:underline;">AI哲学</span></div>
      <div class="text fontBig" style="marginTop:70px;">AI是否无所不能？在讨论AI的相关领域之前，首先应明确AI到底有多大能耐。</div>
      <div  id="productId"><div style="marginTop:70px;fontSize:22px">一、AI的能力边界</div></div>
      <div class="text fontBig">如果想深入一点，从理论层面来解释 AI 的能力边界，就要把图灵大师搬出来了。图灵（Alan Mathison Turing，1912年6月23日—1954年6月7日）在上世纪30年代中期，就在思考3个问题：</div>
      <div class="text fontBig">1. 世界上是否所有数学问题都有明确的答案？</div>
      <div class="text fontBig">2. 如果有明确的答案，是否可以通过有限的步骤计算出答案？</div>
      <div class="text fontBig">3. 对于那些有可能在有限步骤计算出来的数学问题，能否有一种假象的机械，让它不断运动，最后当机器停下来的时候，那个数学问题就解决了？</div>
      <div class="text fontBig">图灵还真设计出来一套方法，后人称它为图灵机。今天所有的计算机，包括全世界正在设计的新的计算机，从解决问题的能力来讲，都没有超出图灵机的范畴。</div>
      <div class="text fontBig">通过上面的3个问题，图灵已经划出了界限，这个界限不但适用于今天的 AI ，也适用于未来的 AI 。</div>
      <div class="text fontBig">下面我们再进一步把边界清晰的描述一下，如图所示。</div>
      <img style="width:400px;marginLeft:130px" src="../assets/02-01.png" alt="">
      <div class="text fontBig">* 世界上有很多问题，只有一小部分是数学问题</div>
      <div class="text fontBig">* 在数学问题里，只有一小部分是有解的</div>
      <div class="text fontBig">* 在有解的问题中，只有一部分是理想状态的图灵机可以解决的</div>
      <div class="text fontBig">* 在后一部分（图灵机可解决的部分），又只有一部分是今天的计算机可以解决的</div>
      <div class="text fontBig">* 而 AI 可以解决的问题，又只是计算机可以解决问题的一部分。</div>
      <div class="text fontBig">可以这样说，<b>在一些特定场景中， AI 可以表现的很好，但是在大部分场景中，AI 并没有什么用。</b></div>
      <div style="marginTop:70px;fontSize:22px">二、AI涉及主题</div>
      <div class="text fontBig">与AI相关的主题很多，这里只列出一些密切相关的主题，并且也只能通过名称来了解一些。这些主题包括机器学习、数据科学和深度学习。</div>
      <div style="marginTop:55px;fontSize:16px;fontWeight:bold;"  class="text">1. 机器学习</div>
      <div class="text fontBig">机器学习可以说是AI的一个子领域，而AI本身是<b>计算机科学</b>的一个子领域（这种分类往往有点不精确，机器学习的某些部分可以同等地或更好地属于统计）。机器学习使AI解决方案具有自适应性。简明定义如下：</div>
      <div class="text fontBig" style="color:#0090ff;">【关键术语】</div>
      <div class="text fontBig" style="color:#0090ff;"><b>机器学习</b></div>
      <div class="text fontBig" style="color:#0090ff;">通过越来越多的经验或数据来提高完成给定任务的性能的系统。</div>
      <div style="marginTop:55px;fontSize:16px;fontWeight:bold;"  class="text">2. 深度学习</div>
      <div class="text fontBig">深度学习是机器学习的子领域，它本身是AI的子领域，而AI本身是计算机科学的子领域。我们将在第5章中更详细地讨论深度学习，但现在让我们注意的是，深度学习的“深度”是指数学模型的复杂性，现代计算机不断增强的计算能力使研究人员能够增加这种复杂性，使其达到不仅在数量上而且在质量上与以前都不同的水平。如您所知，科学通常涉及许多逐渐增多的特殊子领域，子领域的子领域等等。这使研究人员能够放大到一个特定的主题，从而有可能赶上多年来积累的不断增长的知识量，并产生有关该主题的新知识，或者，去修正先前的知识，使之更准确。</div>
      <div style="marginTop:55px;fontSize:16px;fontWeight:bold;"  class="text">3. 数据科学</div>
      <div class="text fontBig">数据科学是最近出现的、涵盖多个分支学科的总称，包括机器学习、统计信息和计算机科学的某些方面，涉及算法、数据存储和Web应用程序开发。数据科学也是一门实践学科，其问题解决方案通常会涉及一点AI，但也不如头条新闻所预期的那么多。</div>
      <div style="marginTop:55px;fontSize:16px;fontWeight:bold;"  class="text">4. 机器人</div>
      <div class="text fontBig">机器人是指对机器进行类人构建和程序设计，以便它们可以在复杂的现实世界中工作。在某种程度上，机器人是AI的终极挑战，因为它需要将AI的几乎所有领域结合起来。例如：</div>
      <ul>
        <li class="text fontBig">用于感知环境的计算机视觉和语音识别</li>
        <li class="text fontBig">面对不确定性的情形，能进行自然语言处理、信息检索和推理，以便处理指令并预测潜在动作的后果</li>
        <li class="text fontBig">与人类互动和合作的认知建模和情感计算（响应人类情感表达或模拟情感的系统）</li>
      </ul>
      <div class="text fontBig">许多与机器人技术相关的AI问题，通过机器学习能够被最好地解决，这使得机器学习也成为了机器人学科的一个核心分支。</div>
      <img style="width:400px;marginLeft:130px" src="../assets/02-02.png" alt="">
      <div class="text fontBig" style="color:#f15a23;">【注意】</div>
      <div class="text fontBig" style="color:#f15a23;fontWeight:bold">什么是机器人？</div>
      <div class="text fontBig" style="color:#f15a23;">简而言之，机器人是一种机器，由传感器（感知环境）、执行器（对环境起作用）和处理器组成，可以对它们进行编程以执行一系列动作。人们习惯于科幻小说中对机器人的描述，通常会想到机器人以蹒跚的步态行走，以金属般单调的语气说话。目前使用的大多数现实世界机器人看起来都大不相同，因为它们是根据具体的应用而设计的。大多数应用都不一定将机器人设计成人形，就像我们没有将洗碗机做成人形机器人一样，只是使用智能射流来清洗餐具。</div>
      <div class="text fontBig" style="color:#f15a23;">乍一看可能并不明显，但任何一种具有一定程度自主性的车辆（包括传感器、处理器和执行器），也算是机器人。另外，即使主要是基于软件解决方案，如客户服务聊天APP，也可算机器人。</div>
      <div class="text fontBig">机器人是人类认识世界、改造世界的又一种新型工具（参阅人类历史上的工具进化）。</div>
      <div class="text" style="fontSize:16px;color:rgb(51,51,51);marginTop:55px;fontWeight:bold;">【知识巩固】</div>
      <div class="text fontBig" style="fontWeight:bold">练习2：人工智能的分类（Ref/BCAED）</div>
      <div class="text fontBig">分类法是一种对许多可能彼此特殊的事物进行分类的方案。我们已经解释了许多学科或领域之间的关系，并指出，例如，机器学习通常被认为是AI的一个子领域。</div>
      <div class="text fontBig">可视化分类法的便捷方法是欧拉图。欧拉图（与更熟悉的维恩图密切相关）由与概念相对应的形状组成，这些形状被组织为使形状之间的重叠对应于概念之间的重叠。</div>
      <div class="text fontBig">注意，分类法不必严格地分层。一门学科可以是一个或多个一般性主题的子领域，例如，机器学习也可以被视为统计学的子领域。在这种情况下，子领域的概念将放在较笼统的主题之间的重叠中。</div>
      <div class="text fontBig"><b>您的任务：在下面给出的欧拉图示例中构建一个分类法，显示以下各项之间的关系：AI、机器学习、计算机科学、数据科学和深度学习。</b></div>
      <img style="width:400px;marginLeft:130px" src="../assets/02-03.png" alt="">
      <div class="text fontBig">1.你会将人工智能放在哪里？<input style="marginLeft:15px;" type="radio" v-model="chooseone" value="A" :disabled="isDisabled"><label>A</label><input style="marginLeft:15px;" type="radio" v-model="chooseone" value="B" :disabled="isDisabled"><label>B</label><input style="marginLeft:15px;" type="radio" v-model="chooseone" value="C" :disabled="isDisabled"><label>C</label><input style="marginLeft:15px;" type="radio" v-model="chooseone" value="D" :disabled="isDisabled"><label>D</label><input style="marginLeft:15px;" type="radio" v-model="chooseone" value="E" :disabled="isDisabled"><label>E</label></div>
      <div class="text fontBig">2.你会将机器学习放在哪里？<input style="marginLeft:15px;" type="radio" v-model="choosetow" value="A" :disabled="isDisabled"><label>A</label><input style="marginLeft:15px;" type="radio" v-model="choosetow" value="B" :disabled="isDisabled"><label>B</label><input style="marginLeft:15px;" type="radio" v-model="choosetow" value="C" :disabled="isDisabled"><label>C</label><input style="marginLeft:15px;" type="radio" v-model="choosetow" value="D" :disabled="isDisabled"><label>D</label><input style="marginLeft:15px;" type="radio" v-model="choosetow" value="E" :disabled="isDisabled"><label>E</label></div>
      <div class="text fontBig">3.你会将计算机科学放在哪里？<input style="marginLeft:15px;" type="radio" v-model="choosethree" value="A" :disabled="isDisabled"><label>A</label><input style="marginLeft:15px;" type="radio" v-model="choosethree" value="B" :disabled="isDisabled"><label>B</label><input style="marginLeft:15px;" type="radio" v-model="choosethree" value="C" :disabled="isDisabled"><label>C</label><input style="marginLeft:15px;" type="radio" v-model="choosethree" value="D" :disabled="isDisabled"><label>D</label><input style="marginLeft:15px;" type="radio" v-model="choosethree" value="E" :disabled="isDisabled"><label>E</label></div>
      <div class="text fontBig">4.你会将数据科学放在哪里？<input style="marginLeft:15px;" type="radio" v-model="choosefour" value="A" :disabled="isDisabled"><label>A</label><input style="marginLeft:15px;" type="radio" v-model="choosefour" value="B" :disabled="isDisabled"><label>B</label><input style="marginLeft:15px;" type="radio" v-model="choosefour" value="C" :disabled="isDisabled"><label>C</label><input style="marginLeft:15px;" type="radio" v-model="choosefour" value="D" :disabled="isDisabled"><label>D</label><input style="marginLeft:15px;" type="radio" v-model="choosefour" value="E" :disabled="isDisabled"><label>E</label></div>
      <div class="text fontBig">5.你会将深度学习放在哪里？<input style="marginLeft:15px;" type="radio" v-model="choosefive" value="A" :disabled="isDisabled"><label>A</label><input style="marginLeft:15px;" type="radio" v-model="choosefive" value="B" :disabled="isDisabled"><label>B</label><input style="marginLeft:15px;" type="radio" v-model="choosefive" value="C" :disabled="isDisabled"><label>C</label><input style="marginLeft:15px;" type="radio" v-model="choosefive" value="D" :disabled="isDisabled"><label>D</label><input style="marginLeft:15px;" type="radio" v-model="choosefive" value="E" :disabled="isDisabled"><label>E</label></div>
      <button class="sentBtn" @click="sentBtn">提交</button>
      <div class="text fontBig" style="fontWeight:bold;matginTop:55px;">练习3：任务示例（Ref/123,2,13,3,1）</div>
      <div class="text fontBig">请考虑以下示例任务。尝试确定其中涉及与AI相关的字段。选择所有符合条件的选项。（提示：机器学习几乎总是涉及某种统计数据）。</div>
      <div class="text fontBig">注意：本练习旨在激发您思考AI的不同方面及其在各种应用程序中的作用。由于许多问题都没有明确的答案，因此该练习不会包含在评分中。但是，我们还是建议您尽力而为，积极回答，不要担心我们的答案是否会与您的有所不同。</div>
      <div class="text fontBig">1.自动驾驶汽车<input style="marginLeft:15px;" type="radio" v-model="choosesix" value="统计" :disabled="isDisabledOne"><label>统计</label><input style="marginLeft:15px;" type="radio" v-model="choosesix" value="机器人" :disabled="isDisabledOne"><label>机器人</label><input style="marginLeft:15px;" type="radio" v-model="choosesix" value="机器学习" :disabled="isDisabledOne"><label>机器学习</label></div>
      <div class="text fontBig">2.引导火箭进入轨道<input style="marginLeft:15px;" type="radio" v-model="chooseseven" value="统计" :disabled="isDisabledOne"><label>统计</label><input style="marginLeft:15px;" type="radio" v-model="chooseseven" value="机器人" :disabled="isDisabledOne"><label>机器人</label><input style="marginLeft:15px;" type="radio" v-model="chooseseven" value="机器学习" :disabled="isDisabledOne"><label>机器学习</label></div>
      <div class="text fontBig">3.在线广告优化<input style="marginLeft:15px;" type="radio" v-model="chooseeight" value="统计" :disabled="isDisabledOne"><label>统计</label><input style="marginLeft:15px;" type="radio" v-model="chooseeight" value="机器人" :disabled="isDisabledOne"><label>机器人</label><input style="marginLeft:15px;" type="radio" v-model="chooseeight" value="机器学习" :disabled="isDisabledOne"><label>机器学习</label></div>
      <div class="text fontBig">4.客服聊天机器人<input style="marginLeft:15px;" type="radio" v-model="choosenine" value="统计" :disabled="isDisabledOne"><label>统计</label><input style="marginLeft:15px;" type="radio" v-model="choosenine" value="机器人" :disabled="isDisabledOne"><label>机器人</label><input style="marginLeft:15px;" type="radio" v-model="choosenine" value="机器学习" :disabled="isDisabledOne"><label>机器学习</label></div>
      <div class="text fontBig">5.盖洛普结果总结<input style="marginLeft:15px;" type="radio" v-model="chooseten" value="统计" :disabled="isDisabledOne"><label>统计</label><input style="marginLeft:15px;" type="radio" v-model="chooseten" value="机器人" :disabled="isDisabledOne"><label>机器人</label><input style="marginLeft:15px;" type="radio" v-model="chooseten" value="机器学习" :disabled="isDisabledOne"><label>机器学习</label></div>
      <button class="sentBtn" @click="sentBtnOne">提交</button>
      <div class="backBtn" style="margin:20px auto;textAlign:center;">
        <button @click="goBlank" style="cursor: pointer;">上一页</button>
        <button @click="goMune"  style="marginLeft:15px;marginRight:15px;cursor: pointer;">返回目录页</button>
        <button @click="NextClk" style="cursor: pointer;">下一页</button>
      </div>
      
  </div>
</template>

<script>
export default {
  data () {
    return {
      chooseone:'',
      choosetow:'',
      choosethree:'',
      choosefour:'',
      choosefive:'',
      choosesix:'',
      chooseseven:'',
      chooseeight:'',
      choosenine:'',
      chooseten:'',
      isDisabled:false,
      isDisabledOne:false,
    }
  },
  methods:{
    sentBtn(){
      this.isDisabled = true;
    },
    sentBtnOne(){
      this.isDisabledOne = true;
    },
    jumpOne(){
       this.$router.push('/chapterOne');
    },
    jumpTow(){
      const returnEle = document.querySelector("#productId");  //productId是将要跳转区域的id
        if (!!returnEle) {
          returnEle.scrollIntoView(true); // true 是默认的
        }
        document.querySelector("counter1").scrollIntoView(true); //这里的counter1是将要返回地方的id
    },
    jumpThree(){
      this.$router.push('/chapterThree');
    },
    goBlank(){
      this.$router.push('/chapterOne');
    },
    goMune(){
      this.$router.push('/');
    },
    NextClk(){
      this.$router.push('/chapterThree');
    },
    
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.chapterOne{
    width: 650px;
    margin: 0 auto;
    padding: 0px 75px;
}
.text{
    text-indent:2em;
}
.fontBig{
    font-size: 16px;
    color: rgb(102, 102, 102);
    line-height: 30px;
    
}
.sentBtn{
  width: 100px;
  margin-left: 240px;
  margin-top: 25px;
  margin-bottom: 25px;
  cursor: pointer;
}
.backBtn button{
  width: 100px;
}
</style>



// WEBPACK FOOTER //
// src/components/chapterTow.vue